/*

  Page.ts

  Single page UI for Netskrafl/Explo using the Mithril library

  Copyright (C) 2025 Miðeind ehf.
  Author: Vilhjálmur Þorsteinsson

  The Creative Commons Attribution-NonCommercial 4.0
  International Public License (CC-BY-NC 4.0) applies to this software.
  For further information, see https://github.com/mideind/Netskrafl

  This UI is built on top of Mithril (https://mithril.js.org), a lightweight,
  straightforward JavaScript single-page reactive UI library.

  The page is structured into models, actions and views,
  cf. https://github.com/pakx/the-mithril-diaries/wiki/Basic-App-Structure

*/

import { GlobalState } from "./globalstate";
import { m } from "./mithril";
import { View } from "./view";
import { Model, getSettings } from "./model";
import { Actions, createRouteResolver } from "./actions";

export type LoginResult = "success" | "expired" | "error";

export async function main(state: GlobalState, container: HTMLElement): Promise<LoginResult> {
  // The main UI entry point, called from page.html
  if (!container) {
    console.error("No container element found");
    return "error";
  }
  try {
    // Skip initial login - authentication will happen lazily on first API call
    // Create the model, actions and view objects in proper sequence
    const settings = getSettings();
    const model = new Model(settings, state);
    const actions = new Actions(model);
    const view = new View(actions);
    // Run the Mithril router
    const routeResolver = createRouteResolver(actions, view);
    m.route(container, settings.defaultRoute, routeResolver);
  } catch(e) {
    console.error("Exception during initialization: ", e);
    return "error";
  }
  return "success";
}

export function unmount(container: HTMLElement) {
  // Unmount the Mithril UI
  m.mount(container, null);
}
